import api, { ApiResponse } from "./api";
import { Campaign, CampaignPerformance, Media } from "../types/campaign";
import { Template } from "../types/template";

const API_URL = "v1/ad/";

class AdService {
    async create(
        ageMin: string,
        ageMax: string,
        hittingHand: string,
        throwingHand: string,
        city: string,
        state: string,
        country: string,
        positions: string[],
        dailyBudget: string,
        startTime: number | null,
        endTime: number | null,
        objective: number,
        nickname: string,
        title: string,
        headline: string,
        link: string,
        media: string[],
        templateId: number
    ) {
        const response = await api
            .post(API_URL + "create", {
                ageMin,
                ageMax,
                hittingHand,
                throwingHand,
                city,
                state,
                country,
                positions,
                dailyBudget,
                startTime,
                endTime,
                objective,
                nickname,
                title,
                headline,
                link,
                media,
                templateId,
            })
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    return response.data.v?.id ?? -1;
                }
                return -1;
            })
            .catch(() => -1);
        
        return response;
    }

    async update(
        id: number,
        uid: number,
        ageMin: string,
        ageMax: string,
        hittingHand: string,
        throwingHand: string,
        city: string,
        state: string,
        country: string,
        positions: string[],
        dailyBudget: string,
        startTime: number | null,
        endTime: number | null,
        objective: number,
        nickname: string,
        title: string,
        headline: string,
        link: string,
        media: string[] | null,
        templateId: number
    ) {
        const response = await api
            .post(API_URL + "update", {
                id,
                uid,
                ageMin,
                ageMax,
                hittingHand,
                throwingHand,
                city,
                state,
                country,
                positions,
                dailyBudget,
                startTime,
                endTime,
                objective,
                nickname,
                title,
                headline,
                link,
                media,
                templateId
            })
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    return response.data.v?.id ?? -1;
                }
                return -1;
            })
            .catch(() => -1);
        
        return response;
    }

    async query() {
        const response = await api
            .post(API_URL + "query/active")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const templatesMap: { [key: string]: any } = response.data.v?.templatesMap ?? {};
                    const templates = Object.values(templatesMap).map((template) => {
                        return new Template(
                            template.id,
                            template.name,
                            template.description,
                            template.contentType,
                            template.transitionType,
                            template.orientation,
                            template.html,
                            0
                        )
                    })

                    const ads: { [key: string]: any }[] = response.data.v?.ads ?? [];
                    const campaigns = ads.map((campaign) => {
                        const media = new Media(campaign.media.urls, campaign.media.media);
                        return new Campaign(
                            campaign.id,
                            campaign.title,
                            campaign.nickname,
                            campaign.adStatus,
                            campaign.approvalStatus,
                            campaign.adminMsg,
                            media,
                            campaign.startTime,
                            campaign.endTime,
                            campaign.objective,
                            campaign.dailyBudget,
                            campaign.ageMin,
                            campaign.ageMax,
                            campaign.hittingHand,
                            campaign.throwingHand,
                            campaign.positions,
                            campaign.country,
                            campaign.state,
                            campaign.city,
                            campaign.headline,
                            campaign.link,
                            campaign.templateId,
                            campaign.impressions,
                            campaign.clicks,
                            campaign.conversions,
                            campaign.spend,
                            campaign.qualityImpressions,
                        )
                    })

                    return { templates: templates, campaigns: campaigns }
                }
                
                return { templates: [], campaigns: [] };
            })
            .catch(() => {
                return { templates: [], campaigns: [] };
            });
        
        return response;
    }

    async queryAll() {
        const response = await api
            .post(API_URL + "query/all")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const ads: { [key: string]: any }[] = response.data.v?.ads ?? [];
                    return ads.map((campaign) => {
                        const media = new Media(campaign.media.urls, campaign.media.media);
                        return new Campaign(
                            campaign.id,
                            campaign.title,
                            campaign.nickname,
                            campaign.adStatus,
                            campaign.approvalStatus,
                            campaign.adminMsg,
                            media,
                            campaign.startTime,
                            campaign.endTime,
                            campaign.objective,
                            campaign.dailyBudget,
                            campaign.ageMin,
                            campaign.ageMax,
                            campaign.hittingHand,
                            campaign.throwingHand,
                            campaign.positions,
                            campaign.country,
                            campaign.state,
                            campaign.city,
                            campaign.headline,
                            campaign.link,
                            campaign.templateId,
                        )
                    })
                } else {
                    return [];
                }
            })
            .catch(() => []);
        
        return response;
    }

    async queryPerformance(adId: number, startTime: number, endTime: number) {
        const response = await api
            .post(API_URL + "query/performance", {
                adId, startTime, endTime
            })
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const v = response.data.v;
                    return new CampaignPerformance(
                        v.impressions,
                        v.clicks,
                        v.qualityImpressions,
                        v.platformImpressions,
                        v.countryImpressions
                    )
                }
                
                return null;
            })
            .catch(() => null);
        
        return response;
    }

    async queryAllPerformance(startTime: number, endTime: number) {
        const response = await api
            .post(API_URL + "query/all/country_platform", {
                startTime, endTime
            })
            .then((response: ApiResponse) => {
                return {
                    country: response.data?.v?.country ?? {},
                    platform: response.data?.v?.platform ?? {}
                };
            })
            .catch(() => {
                return { country: {}, platform: {} };
            });

        
        return response;
    }

    async submit(adId: number) {
        const response = await api
            .post(API_URL + "submit", {
                adId
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
        
        return response;
    }

    async updateNull(adId: number) {
        const response = await api
            .post(API_URL + "update/status/null", {
                adId
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
        
        return response;
    }

    async updateDisabled(adId: number) {
        const response = await api
            .post(API_URL + "update/status/disabled", {
                adId
            })
            .then((response: ApiResponse) => response.data.c === 1)
            .catch(() => false);
        
        return response;
    }

    async queryTemplate() {
        const response = await api
            .post(API_URL + "template/query")
            .then((response: ApiResponse) => {
                if (response.data.c === 1) {
                    const templates: { [key: string]: any }[] = response.data.v ?? [];
                    return templates.map(template => {
                        return new Template(
                            template.id,
                            template.name,
                            template.description,
                            template.contentType,
                            template.transitionType,
                            template.orientation,
                            template.html,
                            template.updatedAt,
                            template.archivedAt,
                            null,
                            null,
                        )
                    })
                }

                return [];
            })
            .catch(() => {
                return [];
            })
        
        return response;
    }
}

const adService = new AdService();

export default adService;
